import React, { useState, useEffect, Fragment } from 'react';
import { RouteComponentProps, Link, navigate, Location } from '@reach/router';
import Input from 'components/Form/Input';
import { UserKeys, User } from 'state/user/UserTypes';
import { RFC } from 'state/StateTypes';
import { LoginRequest, InputChange, FormSubmission, Error, ErrorCodes } from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from 'state/store';
import LogoLgLight from 'assets/logo-neuvana-dark-x3.png';
import styled from 'styled-components';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import Form from 'components/Form';
import Header from 'components/Header';
import TextDecorationSpan from 'components/TextDecorationSpan';
import { loginUser } from 'state/user/UserActions';
import { selectCurrentUser, selectIsAuthenticated } from 'state/user/UserSelectors';
import { RESET_REQUESTED_PATHNAME } from 'state/PathnameReducer';
import { getParamFromUrl, presentErrorBasedOnCode } from 'utils';
import Instructions from 'components/Instructions';
import { theme } from 'styles/ThemeProvider';

const initialFormState: LoginRequest = {
   [UserKeys.username]: '',
   [UserKeys.password]: '',
   [UserKeys.newPassword]: undefined
}

const Login: RFC<RouteComponentProps> = () => {
   const dispatch = useDispatch();
   const [credentials, setCredentials] = useState<LoginRequest>(initialFormState);
   const authenticated: boolean = useSelector((state: AppState) => selectIsAuthenticated(state));
   const currentUser: User | null = useSelector((state: AppState) => selectCurrentUser(state));
   const [loading, setLoading] = useState<boolean>(false);
   const [formComplete, setFormComplete] = useState<boolean>(false);
   const selectedPathname: string = useSelector(({ requestedPathname }: AppState) => requestedPathname);
   const [settingPassword, setSettingPassword] = useState<boolean>(false);
   const [error, setError] = useState<Error | null>(null);
   const [newUser, setNewUser] = useState<{ email: string | undefined; tempPassword: string | undefined }>({ email: undefined, tempPassword: undefined });

   useEffect(() => {
      //if the user is authenticated, arriving at root url & not setting first time password or verifying email
      if (authenticated && window.location.pathname === '/' && !(getParamFromUrl(selectedPathname, 'code') && getParamFromUrl(window.location.href, 'email'))) {
         navigate('/profile');
      }

      return () => {
         error && setError(null)
         setCredentials(initialFormState)
      }
   }, []);

   useEffect(() => {
         setNewUser({ email: getParamFromUrl(window.location.href, 'email'), tempPassword: getParamFromUrl(window.location.href, 'password')})
         //if password included in queryString user is setting password for first time
         if (newUser.tempPassword && newUser.email) {
            setSettingPassword(true);
            setCredentials(credentials => ({ ...credentials, username: newUser.email, password: newUser.tempPassword }));
         }
   }, [newUser.email, newUser.tempPassword, window.location.search]);

   useEffect(() => {
      (async () => {
         if (error && error.code === ErrorCodes.NEW_PASSWORD_REQUIRED) {
            await navigate(`?email=${credentials.username}&password=${credentials.password}`);
         }
      })();
   }, [error]);

   useEffect(() => {
      if (currentUser) {
         setLoading(false);
         navigate(settingPassword ? '/profile' : selectedPathname, { replace: true, state: {} });
         const verificationCode: string | undefined = getParamFromUrl(selectedPathname, 'code');
         !verificationCode && dispatch({ type: RESET_REQUESTED_PATHNAME });
      }
   }, [currentUser]);

   useEffect(() => {
      const targetPassword: string | undefined = settingPassword ? credentials[UserKeys.newPassword] : credentials[UserKeys.password];
      if (!!credentials.username && targetPassword && targetPassword.length >= 8) {
         setFormComplete(true);
      } else {
         setFormComplete(false);
      }
   }, [credentials]);

   const handleLogin = async (e: FormSubmission): Promise<void> => {
      error && setError(null)
      e.preventDefault();
      try {
         setLoading(true);
         await dispatch(loginUser(credentials));
      } catch (error) {
         //if error results from missing token it will not have a json method
         error = error.json ? await error.json() : error;
         setError(error);
      }
      setLoading(false);
   };

   const handleInput = (e: InputChange): void => {
      e.persist();
      setError(null);
      setCredentials(credentials => ({ ...credentials, [e.target.name]: e.target.value }));
   };

   const DisplayErrorMessage: RFC<{ navBasedError?: string }> = ({ navBasedError }) => error!.code === ErrorCodes.EXPIRED_CODE ? (
      <ExpiredCodeAlert>Your password reset code has expired <br />
         <ForgotPasswordLink to="/forgot-password">Click here</ForgotPasswordLink> <span style={{ color: theme.primaryBlueDark }}>to request a new one</span>
      </ExpiredCodeAlert>
   ) : (
      <ErrorMessage>{navBasedError ?? presentErrorBasedOnCode(error?.code as ErrorCodes)}</ErrorMessage>
   )

   return (
      <Container>
         <Header>
            <img src={LogoLgLight} alt="Neuvana Logo" height={200} width={200} />
         </Header>
         <Location>
            {({ location }) => (
               <FormContainer>
                  <Form onSubmit={handleLogin}>
                     {!settingPassword ? (
                        <Fragment>
                           <Input
                              label="Email"
                              value={credentials.username}
                              name={UserKeys.username}
                              type="email"
                              onChange={handleInput}
                              size="full"
                              errorMessage="Username is required"
                           />
                           <Input
                              label="Password"
                              type="password"
                              value={credentials.password}
                              name={UserKeys.password}
                              onChange={handleInput}
                              size="full"
                              errorMessage="Password is required"
                           />
                        </Fragment>
                     ) : (
                        <Fragment>
                           <Instructions>Please set your preferred password</Instructions>
                           <Input
                              label="New Password"
                              value={credentials.newPassword}
                              onChange={handleInput}
                              type="password"
                              size="full"
                              name="newPassword"
                              errorMessage="New password is required"
                           />
                        </Fragment>
                     )}
                     <Button isLoading={loading} disabled={!formComplete || loading || Boolean(error)}>
                        {!settingPassword ? 'LOGIN' : 'SET PASSWORD'}
                     </Button>
                  </Form>
                  {error?.code && error.code !== ErrorCodes.NEW_PASSWORD_REQUIRED && (
                     <DisplayErrorMessage navBasedError={location.state?.message} />
                  )}
                  {!settingPassword && (
                     <ForgotPasswordLink to="/forgot-password">
                        <TextDecorationSpan>Forgot Password?</TextDecorationSpan>
                     </ForgotPasswordLink>
                  )}
               </FormContainer>
            )}
         </Location>
      </Container>
   );
};

export default Login;

const ForgotPasswordLink = styled(Link)`
   font-size: 1em;
   font-weight: 700;
   margin-top: 10px;
   padding-bottom: 4px;
`;

const ExpiredCodeAlert = styled.span`
   color: ${({ theme }) => theme.secondaryRed};
   font-size: 1.1em;
   font-weight: 600;
   a {
      font-weight: bolder;
      color: ${({ theme }) => theme.secondaryBlueDark};
   }
`